import { DAY_KEYS, type Closure, type DayKey, type TimeRange, type WeeklyHours } from '@/types/restaurant';

const COUNTRY_TZ: Record<string, string> = {
  FR: 'Europe/Paris',
  BE: 'Europe/Paris',
  LU: 'Europe/Paris',
  CH: 'Europe/Zurich',
  CN: 'Asia/Shanghai',
  US: 'America/New_York',
  GB: 'Europe/London',
  IT: 'Europe/Rome',
  ES: 'Europe/Madrid',
  PT: 'Europe/Lisbon',
  NL: 'Europe/Amsterdam',
  DE: 'Europe/Berlin',
  CA: 'America/Toronto',
  JP: 'Asia/Tokyo',
  AU: 'Australia/Sydney',
  // DF-0330 — DOM/TOM: distinct ISO country codes, distinct from Europe/Paris.
  GP: 'America/Guadeloupe',
  MQ: 'America/Martinique',
  GF: 'America/Cayenne',
  RE: 'Indian/Reunion',
  YT: 'Indian/Mayotte',
  PM: 'America/Miquelon',
  BL: 'America/St_Barthelemy',
  MF: 'America/Marigot',
  NC: 'Pacific/Noumea',
  PF: 'Pacific/Tahiti',
  WF: 'Pacific/Wallis',
};
const DEFAULT_TZ = 'Europe/Paris';

export function countryTz(country: string | null | undefined): string {
  if (typeof country !== 'string' || country.length === 0) return DEFAULT_TZ;
  return COUNTRY_TZ[country.toUpperCase()] ?? DEFAULT_TZ;
}

function isValidIanaTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** DF-0330 — fuseau d'un restaurant : `timezone` (IANA) si presente et valide
 *  (ex. un restaurant DOM dont le pays reste "FR" mais l'heure non), sinon la
 *  table pays ci-dessus, sinon Europe/Paris. Le hub resout deja le code postal
 *  DOM cote back ; ce repli ne sert que tant qu'un restaurant n'a pas encore de
 *  `timezone` — comportement inchange pour lui. */
export function restaurantTz(restaurant: { timezone?: string | null; country?: string | null }): string {
  const { timezone } = restaurant;
  if (typeof timezone === 'string' && timezone.length > 0 && isValidIanaTimeZone(timezone)) {
    return timezone;
  }
  return countryTz(restaurant.country);
}

export interface LocalParts {
  weekday: number; // 0 = Monday, 6 = Sunday — matches DAY_KEYS order
  hh: number;
  mm: number;
  year: number;
  month: number; // 1-12
  day: number;
}

const FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = FORMATTER_CACHE.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hour12: false,
    });
    FORMATTER_CACHE.set(tz, f);
  }
  return f;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
};

export function localParts(date: Date, tz: string): LocalParts {
  const parts = partsFormatter(tz).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  let hh = parseInt(get('hour'), 10);
  if (hh === 24) hh = 0; // Intl can return "24" for midnight in en-GB
  return {
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
    hh,
    mm: parseInt(get('minute'), 10),
    year: parseInt(get('year'), 10),
    month: parseInt(get('month'), 10),
    day: parseInt(get('day'), 10),
  };
}

/** SC-1081 — jour civil (YYYY-MM-DD) du restaurant a l'instant `now` : un
 *  restaurant de Cayenne a 22:30 est encore la veille d'un navigateur parisien. */
export function todayIsoInTz(tz: string, now: Date = new Date()): string {
  const p = localParts(now, tz);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** SC-1081 — heure murale « HH:MM » d'un instant, lue a l'horloge du restaurant. */
export function formatTimeInTz(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: tz });
}

function parseHHMM(s: unknown): { h: number; m: number } | null {
  if (typeof s !== 'string' || s.length !== 5 || s[2] !== ':') return null;
  const h = parseInt(s.slice(0, 2), 10);
  const m = parseInt(s.slice(3), 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
}

function rangesForDay(weekly: WeeklyHours, day: DayKey): TimeRange[] {
  const r = weekly[day];
  return Array.isArray(r) ? r : [];
}

function rangeMinutes(r: TimeRange): { open: number; close: number } | null {
  const o = parseHHMM(r.open);
  const c = parseHHMM(r.close);
  if (!o || !c) return null;
  const open = o.h * 60 + o.m;
  const close = c.h * 60 + c.m;
  if (open >= close) return null;
  return { open, close };
}

export function isInClosureOn(closures: Closure[], date: Date, tz: string): Closure | null {
  const p = localParts(date, tz);
  const today = `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
  for (const c of closures) {
    if (typeof c?.from !== 'string' || typeof c?.to !== 'string') continue;
    if (c.from <= today && today <= c.to) return c;
  }
  return null;
}

export function isOpenAt(weekly: WeeklyHours, date: Date, tz: string, closures?: Closure[]): boolean {
  if (closures && isInClosureOn(closures, date, tz)) return false;
  const p = localParts(date, tz);
  const day = DAY_KEYS[p.weekday];
  const nowMin = p.hh * 60 + p.mm;
  for (const r of rangesForDay(weekly, day)) {
    const m = rangeMinutes(r);
    if (!m) continue;
    if (m.open <= nowMin && nowMin < m.close) return true;
  }
  return false;
}

export function isSameDayInTz(a: Date, b: Date, tz: string): boolean {
  const pa = localParts(a, tz);
  const pb = localParts(b, tz);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
}

// Builds a Date for the given local wall-clock parts in the given tz.
// Strategy: compute the offset for that instant by formatting a UTC anchor,
// then iterate once to lock onto the correct offset. Avoids extra deps.
export function dateFromLocal(
  year: number,
  month: number,
  day: number,
  hh: number,
  mm: number,
  tz: string,
): Date {
  const guessUtc = Date.UTC(year, month - 1, day, hh, mm);
  let offsetMs = tzOffsetMs(new Date(guessUtc), tz);
  let result = new Date(guessUtc - offsetMs);
  offsetMs = tzOffsetMs(result, tz);
  result = new Date(guessUtc - offsetMs);
  return result;
}

function tzOffsetMs(at: Date, tz: string): number {
  const p = localParts(at, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hh, p.mm);
  return asUtc - at.getTime();
}

// Returns the close time of the current open range if `date` is inside one,
// else null. Used by the header status badge ("ferme à 23h00").
export function currentClosingAt(
  weekly: WeeklyHours,
  date: Date,
  tz: string,
  closures?: Closure[],
): Date | null {
  if (closures && isInClosureOn(closures, date, tz)) return null;
  const p = localParts(date, tz);
  const day = DAY_KEYS[p.weekday];
  const nowMin = p.hh * 60 + p.mm;
  for (const r of rangesForDay(weekly, day)) {
    const m = rangeMinutes(r);
    if (!m) continue;
    if (m.open <= nowMin && nowMin < m.close) {
      const h = Math.floor(m.close / 60);
      const mn = m.close % 60;
      return dateFromLocal(p.year, p.month, p.day, h, mn, tz);
    }
  }
  return null;
}

export function nextOpeningAt(
  weekly: WeeklyHours,
  date: Date,
  tz: string,
  closures?: Closure[],
): Date | null {
  const start = localParts(date, tz);
  const nowMin = start.hh * 60 + start.mm;
  // Cap at 60 days to handle long vacation periods without infinite-loop risk
  for (let offset = 0; offset < 60; offset += 1) {
    const dayIdx = (start.weekday + offset) % 7;
    const dayKey = DAY_KEYS[dayIdx];
    const base = dateFromLocal(start.year, start.month, start.day, 0, 0, tz);
    const target = new Date(base.getTime() + offset * 24 * 60 * 60 * 1000);
    // Skip days that fall inside a closure
    if (closures && isInClosureOn(closures, target, tz)) continue;
    const starts: number[] = [];
    for (const r of rangesForDay(weekly, dayKey)) {
      const m = rangeMinutes(r);
      if (m) starts.push(m.open);
    }
    starts.sort((a, b) => a - b);
    for (const s of starts) {
      if (offset === 0 && s <= nowMin) continue;
      const tp = localParts(target, tz);
      return dateFromLocal(tp.year, tp.month, tp.day, Math.floor(s / 60), s % 60, tz);
    }
  }
  return null;
}

export function isWeeklyHoursEmpty(weekly: WeeklyHours): boolean {
  for (const day of DAY_KEYS) {
    const r = weekly[day];
    if (Array.isArray(r) && r.length > 0) return false;
  }
  return true;
}
